import {
  afterNextRender,
  DestroyRef,
  Directive,
  ElementRef,
  HostListener,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgControl } from '@angular/forms';

import {
  AI_CHAT_QUESTION_TEXTAREA_MAX_HEIGHT_PX,
  AI_CHAT_QUESTION_TEXTAREA_MIN_HEIGHT_PX,
} from '../ai-chat.constants';

/** Autosize the inner `<textarea>` of `pu-text-area` up to a max height, then allow vertical scroll. */
@Directive({
  standalone: true,
  selector: '[fbChatTextareaAutosize]',
})
export class ChatTextareaAutosizeDirective {
  readonly #host = inject(ElementRef<HTMLElement>);
  readonly #destroyRef = inject(DestroyRef);
  readonly #ngControl = inject(NgControl, { optional: true, self: true });

  #textarea: HTMLTextAreaElement | null = null;

  constructor() {
    afterNextRender(() => this.#bindTextarea());

    const control = this.#ngControl?.control;
    if (control) {
      control.valueChanges
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe(() => queueMicrotask(() => this.resize()));
    }
  }

  @HostListener('input')
  onInput(): void {
    this.resize();
  }

  /** Call after clearing the control from the parent so height resets. */
  resize(): void {
    if (!this.#textarea) {
      this.#textarea = this.#host.nativeElement.querySelector('textarea');
    }
    const ta = this.#textarea;
    if (!ta) {
      return;
    }

    // Temporarily clear inline min-height so scrollHeight reflects the true
    // content height, not a value inflated by global CSS (e.g. Teams project
    // styles leaking into pu-ai-chat-page-webcomponent which has no Shadow DOM).
    ta.style.height = 'auto';
    ta.style.minHeight = '0';
    const contentHeight = ta.scrollHeight;
    const nextHeight = Math.max(
      AI_CHAT_QUESTION_TEXTAREA_MIN_HEIGHT_PX,
      Math.min(contentHeight, AI_CHAT_QUESTION_TEXTAREA_MAX_HEIGHT_PX),
    );
    ta.style.height = `${nextHeight}px`;
    ta.style.minHeight = `${AI_CHAT_QUESTION_TEXTAREA_MIN_HEIGHT_PX}px`;
    ta.style.overflowY =
      contentHeight > AI_CHAT_QUESTION_TEXTAREA_MAX_HEIGHT_PX ? 'auto' : 'hidden';
  }

  #bindTextarea(): void {
    this.#textarea = this.#host.nativeElement.querySelector('textarea');
    if (!this.#textarea) {
      return;
    }

    const ta = this.#textarea;
    ta.style.resize = 'none';
    ta.style.overflowY = 'hidden';
    ta.style.boxSizing = 'border-box';
    // Set inline min/max-height so global CSS cannot override the bounds.
    ta.style.minHeight = `${AI_CHAT_QUESTION_TEXTAREA_MIN_HEIGHT_PX}px`;
    ta.style.maxHeight = `${AI_CHAT_QUESTION_TEXTAREA_MAX_HEIGHT_PX}px`;

    this.resize();
    queueMicrotask(() => this.resize());
  }
}
